import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  ArrowRight,
  Check,
  CloudUpload,
  Copy,
  Download,
  Loader2,
  Lock,
  QrCode,
  Radio,
  Share2,
  ShieldCheck,
  UploadCloud,
  Wifi,
  X,
} from "lucide-react";
import { fromBase64Url } from "@limeshare/lsc1";
import { FileIcon } from "../components/FileIcon";
import {
  connectLimeDropReceiver,
  startLimeDropSender,
  type LimeDropSenderSession,
  type P2PFileMeta,
  type P2PProgress,
} from "../engine/limedrop";
import { SpeedTracker, type TransferStats } from "../engine/speed";
import { formatBytes } from "../util";

export function LimeDrop() {
  const { roomId } = useParams();
  return roomId ? <LimeDropReceiver roomId={roomId} /> : <LimeDropSender />;
}

function LimeDropSender() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [session, setSession] = useState<LimeDropSenderSession | null>(null);
  const [status, setStatus] = useState<"idle" | "waiting" | "connecting" | "transferring" | "done" | "failed">("idle");
  const [statusMsg, setStatusMsg] = useState<string>("");
  const [progress, setProgress] = useState<P2PProgress | null>(null);
  const [stats, setStats] = useState<TransferStats | null>(null);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  const speedTracker = useRef(new SpeedTracker());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      session?.cancel();
    };
  }, [session]);

  const handleStart = async () => {
    if (files.length === 0) return;
    speedTracker.current.start();
    try {
      const sess = await startLimeDropSender(
        files,
        (st, msg) => {
          setStatus(st);
          if (msg) setStatusMsg(msg);
          if (st === "done") {
            try {
              confetti({ particleCount: 70, spread: 60, origin: { y: 0.65 }, colors: ["#00ff66", "#7cff5c", "#5ce1ff"] });
            } catch {
              /* ignore */
            }
          }
        },
        (p) => {
          setProgress(p);
          const st = speedTracker.current.update(p.bytesDone, p.bytesTotal);
          setStats(st);
        },
      );
      setSession(sess);

      import("qrcode")
        .then((m) => m.toDataURL(sess.shareUrl, { margin: 1, width: 220, color: { dark: "#f0f6f0", light: "#0d140e" } }))
        .then(setQr)
        .catch(() => setQr(null));
    } catch (e) {
      setStatus("failed");
      setStatusMsg((e as Error).message);
    }
  };

  const copy = async () => {
    if (!session) return;
    await navigator.clipboard.writeText(session.shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloudFallback = () => {
    session?.cancel();
    navigate("/", { state: { fallbackFiles: files } });
  };

  const total = files.reduce((acc, f) => acc + f.size, 0);

  return (
    <section className="panel glow">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,255,102,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00ff66" }}>
          <Radio size={20} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, letterSpacing: "-0.02em" }}>LimeDrop — Direct P2P Pipe</h2>
          <p className="hint" style={{ margin: 0 }}>
            Send directly browser-to-browser over local Wi-Fi or WebRTC. 0 cloud storage, uncapped local speed.
          </p>
        </div>
      </div>

      {status === "idle" && (
        <>
          <div
            className="dropzone"
            style={{ marginTop: 20 }}
            onClick={() => inputRef.current?.click()}
          >
            <div className="dropzone-icon">
              <Wifi size={28} />
            </div>
            <strong>Select files for direct peer-to-peer transmission</strong>
            <small>Streams chunk-by-chunk in RAM using WebRTC DataChannels</small>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) setFiles(Array.from(e.target.files));
                e.target.value = "";
              }}
            />
          </div>

          {files.length > 0 && (
            <ul className="files">
              {files.map((f, i) => (
                <li className="file" key={i}>
                  <div className="file-info">
                    <FileIcon name={f.name} type={f.type} size={20} />
                    <span className="name">{f.name}</span>
                    <span className="size">{formatBytes(f.size)}</span>
                  </div>
                  <button className="btn ghost btn-sm btn-icon" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {files.length > 0 && <div className="hint" style={{ marginTop: 12 }}>{`${files.length} file(s) · ${formatBytes(total)}`}</div>}

          <div className="row" style={{ marginTop: 22 }}>
            <button className="btn block" disabled={files.length === 0} onClick={handleStart}>
              <Radio size={16} /> Open Direct LimeDrop Link
            </button>
          </div>
        </>
      )}

      {status !== "idle" && session && (
        <div style={{ marginTop: 20 }}>
          <div className="alert ok" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Radio size={18} color="#00ff66" />
            <span><b>Status:</b> {statusMsg}</span>
          </div>

          <div className="linkbox" style={{ marginTop: 16 }}>
            <input readOnly value={session.shareUrl} onFocus={(e) => e.currentTarget.select()} />
            <button className="btn" onClick={copy}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? "Copied!" : "Copy link"}</span>
            </button>
          </div>

          <div className="share-strip">
            <button className="btn ghost" onClick={() => setShowQr(!showQr)}>
              <QrCode size={14} /> {showQr ? "Hide QR" : "Show QR Code"}
            </button>
            <button className="btn ghost" onClick={handleCloudFallback}>
              <CloudUpload size={14} /> Fallback to Cloud Upload
            </button>
          </div>

          {showQr && qr && (
            <div style={{ textAlign: "center", margin: "16px 0", padding: 12, background: "rgba(0,0,0,0.5)", borderRadius: 12, display: "inline-block" }}>
              <img src={qr} alt="LimeDrop QR" width={220} height={220} style={{ borderRadius: 8, display: "block" }} />
              <small className="hint" style={{ marginTop: 6, display: "block" }}>Scan on receiving device</small>
            </div>
          )}

          {status === "transferring" && progress && (
            <div className="progress" style={{ marginTop: 18 }}>
              <div className="bar">
                <div style={{ width: `${stats?.percent ?? 0}%` }} />
              </div>
              <div className="meta">
                <span>Streaming {progress.fileName} ({progress.fileIndex + 1}/{session.files.length})</span>
                <div className="progress-stats">
                  {stats?.speedFormatted && <span>{stats.speedFormatted}</span>}
                  <span>{stats?.percent ?? 0}%</span>
                </div>
              </div>
            </div>
          )}

          {status === "done" && (
            <div className="alert ok" style={{ marginTop: 18 }}>
              <Check size={18} /> Direct P2P transfer completed successfully! Both browsers can now close this tab.
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function LimeDropReceiver({ roomId }: { roomId: string }) {
  const [status, setStatus] = useState<"connecting" | "receiving" | "done" | "failed">("connecting");
  const [statusMsg, setStatusMsg] = useState<string>("Connecting to peer…");
  const [incomingFiles, setIncomingFiles] = useState<P2PFileMeta[]>([]);
  const [progress, setProgress] = useState<P2PProgress | null>(null);
  const [stats, setStats] = useState<TransferStats | null>(null);

  const speedTracker = useRef(new SpeedTracker());

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const m = /k=([A-Za-z0-9_-]+)/.exec(hash);
    if (!m) {
      setStatus("failed");
      setStatusMsg("Encryption key missing in link fragment.");
      return;
    }
    const masterKey = fromBase64Url(m[1]!);

    speedTracker.current.start();
    const sessionPromise = connectLimeDropReceiver(
      roomId,
      masterKey,
      async (files) => {
        setIncomingFiles(files);
        return true; // auto-accept
      },
      (st, msg) => {
        setStatus(st);
        if (msg) setStatusMsg(msg);
        if (st === "done") {
          try {
            confetti({ particleCount: 70, spread: 60, origin: { y: 0.65 }, colors: ["#00ff66", "#7cff5c", "#5ce1ff"] });
          } catch {
            /* ignore */
          }
        }
      },
      (p) => {
        setProgress(p);
        const st = speedTracker.current.update(p.bytesDone, p.bytesTotal);
        setStats(st);
      },
    );

    return () => {
      sessionPromise.then((s) => s.cancel()).catch(() => undefined);
    };
  }, [roomId]);

  const total = incomingFiles.reduce((acc, f) => acc + f.size, 0);

  return (
    <section className="panel glow">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,255,102,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00ff66" }}>
          <Radio size={20} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>LimeDrop — Incoming Direct Stream</h2>
          <p className="hint" style={{ margin: 0 }}>Streaming directly from sender's device. No cloud storage used.</p>
        </div>
      </div>

      <div className="alert ok" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {status === "connecting" && <Loader2 size={18} className="animate-spin" />}
        {status === "receiving" && <Radio size={18} color="#00ff66" />}
        {status === "done" && <Check size={18} color="#00ff66" />}
        <span>{statusMsg}</span>
      </div>

      {incomingFiles.length > 0 && (
        <ul className="files" style={{ marginTop: 18 }}>
          {incomingFiles.map((f, i) => (
            <li className="file" key={i}>
              <div className="file-info">
                <FileIcon name={f.name} type={f.type} size={20} />
                <span className="name">{f.name}</span>
                <span className="size">{formatBytes(f.size)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {status === "receiving" && progress && (
        <div className="progress" style={{ marginTop: 18 }}>
          <div className="bar">
            <div style={{ width: `${stats?.percent ?? 0}%` }} />
          </div>
          <div className="meta">
            <span>Receiving &amp; saving {progress.fileName}</span>
            <div className="progress-stats">
              {stats?.speedFormatted && <span>{stats.speedFormatted}</span>}
              <span>{stats?.percent ?? 0}%</span>
            </div>
          </div>
        </div>
      )}

      {status === "done" && (
        <div className="alert ok" style={{ marginTop: 20 }}>
          <Check size={18} /> All files received and decrypted to disk directly from peer!
        </div>
      )}
    </section>
  );
}
